"use client";

import { useIsShopsite } from "@context/DomainContext";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import * as SelectPrimitive from "@radix-ui/react-select";
import { joinClassNames } from "@utils/helpers";
import * as React from "react";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={joinClassNames(
      "flex gap-1 h-10 w-full items-center justify-between rounded-md shadow-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 focus:ring-indigo-500 bg-white hover:bg-gray-100 cursor-default placeholder:text-gray-600",
      className ?? ""
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const isShopsite = useIsShopsite();

  return (
    <SelectPrimitive.Portal
      container={document.getElementById(
        isShopsite ? "shopsite-root-element" : "whitelabel-root-element"
      )}
    >
      <SelectPrimitive.Content
        ref={ref}
        className={joinClassNames(
          "relative z-[100] min-w-[8rem] bg-white border border-gray-300 rounded-md shadow-sm",
          className ?? ""
        )}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="flex items-center justify-center h-6 text-black cursor-default">
          <ChevronUpIcon className="w-5 h-5" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className="p-1.5">
          {children}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex items-center justify-center h-6 text-black cursor-default">
          <ChevronDownIcon className="w-5 h-5" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={joinClassNames(
      "flex items-center h-6 pl-6 pr-9 text-sm leading-none text-gray-500",
      className ?? ""
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={joinClassNames(
      "relative flex items-center h-6 pl-6 text-sm leading-none text-black rounded select-none pr-9 focus:outline-none focus:bg-indigo-500 focus:text-indigo-50",
      className ?? ""
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="absolute left-0 w-6 cursor-default">
      <CheckIcon className="w-4 h-4" />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={joinClassNames("my-1 h-px bg-gray-200", className ?? "")}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
